/* ==========================================================================
   DISCOUNT CODE BOX — drawer and cart page.

   ⚠ THE ENDPOINT, AND THE THREE NAMES THAT LOOK RIGHT AND ARE NOT.
   Applying and removing both go through POST /cart/update.js with a
   `discount` parameter — SINGULAR, a STRING:

       {"discount": "CAMPNOW5"}   apply, replacing whatever was there
       {"discount": ""}           remove every code

   `discount_codes: []`, `discount_code: ''` and form-encoded
   `discount_codes[]=` are ALL accepted with HTTP 200 and SILENTLY IGNORED.
   Measured on live, 15 September: the 200 reads exactly like success and the
   cart comes back unchanged. If removal ever appears to stop working, check
   the parameter name before anything else.

   Preferred over the /discount/CODE?redirect=/cart.js route, which also
   works: this one is documented, returns the updated cart in the response
   body (one request, no follow-up /cart.js), REPLACES rather than
   accumulating, and measured faster — 571-714ms against 702-1957ms.

   ⚠ IT REPLACES THE WHOLE LIST. Sending a second code drops the first unless
   both are sent comma-separated. We deliberately do NOT build multi-code
   support: one code at a time, and a new code replaces the old one, which is
   the endpoint's own behaviour rather than a rule we impose.

   ⚠ REMOVING A CODE RESTORES THE AUTOMATIC 15%. Verified: an order-type code
   cancels the accessory discount (total_discount 1935 -> 3235 on the test
   basket, line-level allocations gone), and {"discount": ""} brings it back
   (3235 -> 1935, line allocations restored). That is what makes the x button
   worth having rather than a warning.

   ⚠ SHOPIFY SUPPLIES NO WORDING FOR A REJECTED CODE. The response is 200 with
   discount_codes: [{code, applicable: false}] and no message or description
   anywhere in the body. The copy is ours, approved by the client, and it
   deliberately does not guess at a reason: `applicable: false` covers
   mistyped, expired, wrong basket and needs-an-account alike.
   ========================================================================== */
(function () {
  'use strict';

  var ROOT = '[data-bc-discount]';

  /* Under the market's route root, never a bare /cart path — see cartRoute
     in bc-cart-item.js. */
  function cartRoute(path) {
    return ((window.Shopify && Shopify.routes && Shopify.routes.root) || '/') + path;
  }

  /* ⚠ DECODE HTML ENTITIES. Shopify's `t` filter HTML-escapes every locale
     string whose key does not end in _html, so `can't` arrives in
     theme.strings as `can&#39;t`. Written straight into textContent it
     renders the entity literally, on screen, to the shopper.

     No existing theme.strings value happened to contain an apostrophe, an
     ampersand or a quote, so nothing had ever exposed it — the client's
     approved invalid-code wording is the first. Decoding here rather than
     renaming the key to _html keeps the copy exactly as approved (the
     Langify key is the rendered string, so the character matters) and covers
     every future string without anyone having to remember this. */
  function decode(str) {
    if (!str || str.indexOf('&') === -1) return str;
    var el = document.createElement('textarea');
    el.innerHTML = str;
    return el.value;
  }

  function t(key, fallback) {
    var s = window.theme && theme.strings;
    return decode((s && s[key]) || fallback);
  }

  function parts(form) {
    return {
      input:   form.querySelector('[data-bc-discount-input]'),
      apply:   form.querySelector('[data-bc-discount-apply]'),
      label:   form.querySelector('[data-bc-discount-apply-label]'),
      message: form.querySelector('[data-bc-discount-message]'),
      applied: form.querySelector('[data-bc-discount-applied]'),
      code:    form.querySelector('[data-bc-discount-code]'),
      remove:  form.querySelector('[data-bc-discount-remove]')
    };
  }

  function say(form, text, kind) {
    var p = parts(form).message;
    if (!p) return;
    if (!text) { p.hidden = true; p.textContent = ''; p.className = 'bc-discount__message'; return; }
    p.textContent = text;
    p.className = 'bc-discount__message is-' + (kind || 'error');
    p.hidden = false;
  }

  /* Pending state. The button is disabled for the round trip and says so —
     measured 571-714ms on the endpoint itself but up to ~2s observed end to
     end, which is long enough to look dead and get double-submitted. The
     input is left enabled so a shopper can keep editing a typo. */
  function busy(form, on) {
    var p = parts(form);
    if (!p.apply) return;
    p.apply.disabled = on;
    form.classList.toggle('is-busy', on);
    if (p.label) {
      if (on) {
        p.label.dataset.idle = p.label.dataset.idle || p.label.textContent;
        p.label.textContent = t('cartDiscountApplying', 'Applying…');
      } else if (p.label.dataset.idle) {
        p.label.textContent = p.label.dataset.idle;
      }
    }
    if (p.remove) p.remove.disabled = on;
  }

  /* ONE APPLIED CODE, from /cart.js's own applicable flag.
     Codes with applicable:false are never shown. That is the whole of the
     stale-failure rule: a code typed yesterday sits in cart state forever
     with applicable:false, and rendering it would surface a failure the
     shopper never asked about today. Errors appear ONLY in direct response
     to a submit in this session. */
  function render(form, cart) {
    var p = parts(form);
    if (!p.applied) return;
    var live = (cart && cart.discount_codes || []).filter(function (d) { return d.applicable; });
    if (!live.length) {
      p.applied.hidden = true;
      if (p.code) p.code.textContent = '';
      return;
    }
    if (p.code) p.code.textContent = live[0].code;
    p.applied.hidden = false;
  }

  function post(discount) {
    return fetch(cartRoute('cart/update.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ discount: discount })
    }).then(function (r) {
      if (!r.ok) throw new Error('HTTP ' + r.status);
      return r.json();
    });
  }

  function readCart() {
    return fetch(cartRoute('cart.js'), { cache: 'no-store' }).then(function (r) { return r.json(); });
  }

  /* ⚠ THE REFRESH, AND WHY THE TWO SURFACES DIFFER.

     DRAWER: dispatch `cart:build`. theme.js listens for it (theme.js:2036),
     calls buildCart(), which replaces [data-products]; bc-cart-drawer.js's
     observer on that node then syncs the footer and dispatches
     bc:cart:updated, which the cart FBT re-renders on. One event, and rows,
     count, both totals and the FBT all follow. No theme.js edit.

     CART PAGE: reload, exactly as assets/bc-cart-item.js does after a
     successful change there and for the same reason — cart:build replaces
     only [data-products], [data-discounts] and the subtotal, while the
     cart-level discount lines, EcoCart and Carma are rendered once at page
     load and never refreshed. Applying a code is precisely a cart-level
     change, so an ajax refresh would leave the page contradicting itself. */
  /* The cart page reloads, so a message shown just before it would be wiped.
     Stash it and re-show it on the way back in. Wrapped because sessionStorage
     throws in a private window, and a lost message must not cost the refresh. */
  var MSG_KEY = 'bcDiscountMessage';

  function stashMessage(text, kind) {
    try { sessionStorage.setItem(MSG_KEY, JSON.stringify({ text: text, kind: kind })); } catch (e) {}
  }

  function restoreMessage() {
    var raw = null;
    try { raw = sessionStorage.getItem(MSG_KEY); sessionStorage.removeItem(MSG_KEY); } catch (e) {}
    if (!raw) return;
    var m; try { m = JSON.parse(raw); } catch (e) { return; }
    if (!m || !m.text) return;
    var form = document.querySelector('[data-bc-discount-location="page"]') || document.querySelector(ROOT);
    if (form) say(form, m.text, m.kind);
  }

  function refresh(form, message, kind) {
    if (form.getAttribute('data-bc-discount-location') === 'page') {
      if (message) stashMessage(message, kind);
      window.location.reload();
      return true;                       // caller stops; the page is going
    }
    document.dispatchEvent(new CustomEvent('cart:build'));
    return false;
  }

  function apply(form) {
    var p = parts(form);
    if (!p.input) return;

    /* Trim and refuse empty. Not cosmetic: /discount/%20 was measured adding
       a literal " " entry to the cart's code list, and an empty submit here
       would ask the endpoint to clear a code the shopper never meant to
       touch, since "" is the REMOVE payload. */
    var code = (p.input.value || '').trim();
    if (!code) { p.input.focus(); return; }

    say(form, '');
    busy(form, true);

    readCart().then(function (before) {
      var was = before.total_discount || 0;
      return post(code).then(function (cart) {
        var entry = (cart.discount_codes || []).filter(function (d) {
          return d.code.toLowerCase() === code.toLowerCase();
        })[0];

        if (!entry || !entry.applicable) {
          /* Rejected. Clear the field so it cannot read as applied — the
             client's instruction, and it also stops a second tap re-sending
             a code we have just been told does not work. */
          p.input.value = '';
          var msg = t('cartDiscountInvalid', "That code can't be used with this basket.");
          say(form, msg, 'error');
          render(form, cart);

          /* ⚠ REFRESH HERE TOO, AND THIS IS NOT BELT AND BRACES.
             `discount` REPLACES the whole code list, so submitting a code that
             turns out to be inapplicable still DROPS whatever was applied
             before it. That brings back any automatic discount the old code
             had cancelled, changes the totals, and — because a line's key
             incorporates its discount allocations — RE-SPLITS lines that had
             merged. Measured: a working code on a 5-item basket merged two
             tent lines into one of qty 3; a rejected code submitted next
             restored the 15%, split them back to qty 1 + qty 2, and the
             drawer went on showing the merged row and a stale subtotal.

             Returning early here was the bug. Every /cart/update.js call can
             change the cart whatever it says about the code. */
          refresh(form, msg, 'error');
          return;
        }

        p.input.value = '';
        render(form, cart);

        /* Better, equal or worse is decided on the NUMBER, not the code type.
           An order-type code cancels the automatic 15% but can still leave the
           shopper better off — CAMPNOW5 on the test basket saved 3235 against
           the 15%'s 1935 — so only a measured fall is worth a word. */
        var now = cart.total_discount || 0;
        var note = null;
        if (now < was) {
          note = t('cartDiscountReplaced',
            'This code replaced your 15% off accessories. Remove it to get the 15% back.');
          say(form, note, 'warn');
        } else {
          say(form, '');
        }
        refresh(form, note, 'warn');
      });
    }).catch(function () {
      say(form, t('cartUpdateError', 'Sorry, we could not update your basket. Please try again.'), 'error');
    }).then(function () {
      busy(form, false);
    });
  }

  function remove(form) {
    say(form, '');
    busy(form, true);
    post('').then(function (cart) {
      render(form, cart);
      refresh(form);
    }).catch(function () {
      say(form, t('cartUpdateError', 'Sorry, we could not update your basket. Please try again.'), 'error');
    }).then(function () {
      busy(form, false);
    });
  }

  /* Delegated on document, so a form that arrives with a re-rendered drawer
     needs no re-binding — the same reasoning as the cart FBT's drag handlers. */
  document.addEventListener('click', function (e) {
    var applyBtn = e.target.closest && e.target.closest('[data-bc-discount-apply]');
    if (applyBtn) { e.preventDefault(); apply(applyBtn.closest(ROOT)); return; }
    var removeBtn = e.target.closest && e.target.closest('[data-bc-discount-remove]');
    if (removeBtn) { e.preventDefault(); remove(removeBtn.closest(ROOT)); }
  });

  /* ⚠ ENTER MUST BE SWALLOWED. Both call sites are inside a form, so Enter in
     this input submits it and navigates to /cart mid-typing. */
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter') return;
    var input = e.target.closest && e.target.closest('[data-bc-discount-input]');
    if (!input) return;
    e.preventDefault();
    apply(input.closest(ROOT));
  });

  function syncAll() {
    var forms = document.querySelectorAll(ROOT);
    if (!forms.length) return;
    readCart().then(function (cart) {
      Array.prototype.forEach.call(forms, function (f) { render(f, cart); });
    }).catch(function () {});
  }

  /* First paint, every drawer open, and after any cart change. CartDrawer:open
     is the same event the wallet harvest waits on, for the same reason:
     anything inside #HeaderCart is display:none until then. */
  function boot() { syncAll(); restoreMessage(); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('CartDrawer:open', syncAll);
  document.addEventListener('bc:cart:updated', syncAll);
})();
